// components/esg-metrics/data-entry/columns.ts
import type { ColumnDef } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'
import DownloadIcon from '~/components/DownloadIcon.vue'
import ExpandIcon from '~/components/ExpandIcon.vue'
import { downloadExcelTemplate } from '@/utils/excel/excel'
import { downloadBlob } from '@/utils/downloadBlob'

export const createDataEntryColumns = (
  unfilteredData: EsgFilterData,
  showMetricsModal: (metrics: EsgMetricItem[]) => void,
  showAssetsModal: (assets: EsgAssetItem[]) => void,
  isClientTemplateEnabled: boolean,
): ColumnDef<EsgTemplateSubmission>[] => [
  {
    accessorKey: 'template.name',
    id: 'name',
    size: 300,
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

      return h('div', { class: 'flex justify-between items-center' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Template'),
        h(Button, {
          class: 'text-main-primary',
          variant: 'ghost',
          onClick: () => column.toggleSorting(isSorted === 'asc', true),
        }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
      ])
    },
    cell: ({ row }) => h('div', { class: 'font-medium no-localize-translate' }, row.getValue('name')),
  },

  // Metrics Covered column
  {
    accessorKey: 'metrics',
    id: 'metric',
    size: 250,
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Metrics Covered',
      filterData: (unfilteredData.metrics || []).map(metric => ({
        id: metric.id || '',
        name: metric.name || '',
      })),

    }),
    cell: ({ row }) => {
      const metrics = row.getValue('metric') as EsgMetricItem[]
      return h('div', { class: 'flex justify-between items-center' }, [
        h('span', {}, `${metrics.length} Metrics`),
        h('button', {
          class: 'p-4 hover:bg-slate-200 rounded-md',
          onClick: () => {
            showMetricsModal(metrics)
          },
        }, [
          h(ExpandIcon, { class: 'h-4 w-4' }),
        ]),
      ])
    },
  },

  // Assets Covered column
  {
    accessorKey: 'assets',
    id: 'asset',
    size: 250,
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Assets Covered',
      filterData: unfilteredData.assets.map(asset => ({
        id: asset.id || '',
        name: asset.name || '',
      })),
    }),
    cell: ({ row }) => {
      const assets = row.getValue('asset') as EsgAssetItem[]
      return h('div', { class: 'flex justify-between items-center' }, [
        h('span', {}, `${assets.length} Assets`),
        h('button', {
          class: 'p-4 hover:bg-slate-200 rounded-md',
          onClick: () => {
            showAssetsModal(assets)
          },
        }, [
          h(ExpandIcon, { class: 'h-4 w-4' }),
        ]),
      ])
    },
  },

  // Action column
  {
    id: 'action',
    size: 150, // Fixed width in pixels
    minSize: 150, // Same as size to prevent resizing smaller
    maxSize: 150, // Same as size to prevent resizing larger
    enableResizing: false,
    header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Action'),
    cell: ({ row }) => {
      const templateId = row.original.template?.id
      const hasCustomTemplate = row.original.has_custom_template === true
      const isDownloadDisabled = isClientTemplateEnabled && !hasCustomTemplate

      return h('div', { class: 'flex items-center' }, [
        h(Button, {
          class: 'flex items-center text-black border-theme-border-grey',
          variant: 'outline',
          size: 'sm',
          onClick: () => {
            navigateTo(`/dashboard/esg-data-entry/upload/${templateId}`)
          },
        }, () => [
          h('span', {
            class: 'text-min',
          }, 'Enter Data'),
        ]),
        h('div', { class: 'relative group' }, [
          h(Button, {
            class: isDownloadDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-200',
            variant: 'link',
            size: 'sm',
            disabled: isDownloadDisabled,
            onClick: () => {
              if (isDownloadDisabled) return
              const { $apis } = useNuxtApp()

              if (isClientTemplateEnabled) {
                $apis.esgMetrics.downloadExternalTemplate(String(templateId))
                  .then((response) => {
                    downloadBlob(response.data as Blob, response.headers['content-disposition'], 'esg-template.xlsx')
                  })
              }
              else {
                $apis.esgMetrics.getTemplateById(templateId)
                  .then((response) => {
                    const template = response.data
                    const columnNames = template.column_headers?.map(col => col.name) || []
                    downloadExcelTemplate(columnNames, template.name || `template-${templateId}`)
                  })
              }
            },
          }, () => h(DownloadIcon, { class: 'h-4 w-4' })),
          isDownloadDisabled
            ? h('div', {
              class: 'absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 px-2 py-1 bg-zuno-primary-2 text-white text-xs rounded whitespace-nowrap mb-1 z-10',
            }, 'No custom template available yet.')
            : null,
        ]),
      ])
    },
  },
]
